import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useAuthStore from '../../context/authStore';
import { useLoader } from '../../components/shared/LoaderContext';

// Indian States & UTs
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const MEMBERSHIP_TYPES = [
  'Life Member (Psychiatrist)',
  'Life Associate Member (Psychologist/Counsellor/Mental Health Social Worker/Physician)',
  'Annual membership',
];

export default function MemberRegistrationPage() {
  const { user, refreshUser } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [memberOptions, setMemberOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  // Profile photo preview
  const [photoPreview, setPhotoPreview] = useState(null);
  const [scannedFiles, setScannedFiles] = useState([]);

  const [form, setForm] = useState({
    // Personal Information
    dateOfBirth: '', dobDay: '', dobMonth: '', dobYear: '',
    age: '', gender: '', nationality: 'Indian',
    profilePhoto: null,
    // Contact Information
    address: '', city: '', state: '', pincode: '', phone: '',
    // Professional Information
    degree: '', university: '', yearOfPassing: '',
    areasOfInterest: '', officeAddress: '', membershipType: '',
    scannedDegrees: [],
    // Referrals
    referral1: null, referral2: null,
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  useEffect(() => {
    if (user?.hasProfile) {
      toast('Registration already submitted');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => { loadMembers(''); }, []);

  const loadMembers = async (inputValue) => {
    setSearchLoading(true);
    try {
      const res = await api.get(`/member/active-members?search=${inputValue}`);
      const opts = res.data.members.map((m) => ({
        value: m.id,
        label: `${m.name} (${m.memberId})`,
      }));
      setMemberOptions(opts);
      return opts;
    } catch { return []; }
    finally { setSearchLoading(false); }
  };

  const handleReferralSearch = (inputValue) => {
    if (inputValue.length >= 1) {
      loadMembers(inputValue);
    } else if (inputValue.length === 0) {
      // Bug fix: reload all members when search is cleared
      loadMembers('');
    }
  };

  // DOB stored as separate Day/Month/Year for guaranteed DD/MM/YYYY display
  const handleDOBPartChange = (part) => (e) => {
    const value = e.target.value;
    const updated = { ...form, [part]: value };
    setForm(updated);

    const { dobDay, dobMonth, dobYear } = { ...form, [part]: value };
    if (dobDay && dobMonth && dobYear) {
      // Build ISO date (YYYY-MM-DD) for backend, but UI always shows DD/MM/YYYY
      const isoDate = `${dobYear}-${dobMonth}-${dobDay}`;
      set('dateOfBirth', isoDate);

      const today = new Date();
      const birth = new Date(isoDate);
      const age = today.getFullYear() - birth.getFullYear() -
        (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
      set('age', age > 0 ? age : '');
    }
  };

  // Profile photo handler
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      set('profilePhoto', file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  // Scanned degrees handler
  const handleScannedDegreesChange = (e) => {
    const files = Array.from(e.target.files);
    setScannedFiles(files);
    set('scannedDegrees', files);
  };

  // Mobile - numeric only
  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    set('phone', val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.referral1 || !form.referral2) {
      toast.error('Please select both referrals');
      return;
    }
    if (form.referral1.value === form.referral2.value) {
      toast.error('Both referrals must be different members');
      return;
    }
    if (form.phone && form.phone.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    setLoading(true);
    const interval = showLoader('Submitting your registration...');
    try {
      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('dateOfBirth', form.dateOfBirth);
      formData.append('age', form.age);
      formData.append('gender', form.gender);
      formData.append('nationality', form.nationality);
      formData.append('address', form.address);
      formData.append('city', form.city);
      formData.append('state', form.state);
      formData.append('pincode', form.pincode);
      formData.append('phone', form.phone);
      formData.append('degree', form.degree);
      formData.append('university', form.university);
      formData.append('yearOfPassing', form.yearOfPassing);
      formData.append('areasOfInterest', form.areasOfInterest);
      formData.append('officeAddress', form.officeAddress);
      formData.append('membershipType', form.membershipType);
      formData.append('referral1Id', form.referral1.value);
      formData.append('referral2Id', form.referral2.value);

      if (form.profilePhoto) {
        formData.append('profilePhoto', form.profilePhoto);
      }
      if (form.scannedDegrees.length > 0) {
        form.scannedDegrees.forEach((file) => {
          formData.append('scannedDegrees', file);
        });
      }

      const res = await api.post('/member/register', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      hideLoader(interval);
      toast.success(`Registration submitted! Your Member ID: ${res.data.memberId}`);
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      hideLoader(interval);
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const sectionTitle = {
    color: 'var(--primary)', fontSize: 14, fontWeight: 700,
    marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em'
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? 'var(--primary-light)' : 'var(--border)',
      fontSize: 14, minHeight: 42,
      boxShadow: state.isFocused ? '0 0 0 3px rgba(37,99,168,0.12)' : 'none',
    }),
    option: (base, state) => ({
      ...base, fontSize: 14,
      background: state.isSelected ? 'var(--primary)' : state.isFocused ? '#f0f4ff' : 'white',
    }),
  };

  return (
    <div>
      <div className="page-header">
        <h2>Membership Registration</h2>
        <p>Complete all sections and select your two referrals to submit your application</p>
      </div>

      <form onSubmit={handleSubmit}>

        {/* ── SECTION 1: Basic Details ─────────────── */}
        <div className="card" style={{ padding: 28, marginBottom: 20 }}>
          <div style={sectionTitle}>1. Basic Details</div>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '14px 16px', marginBottom: 16, fontSize: 13, color: 'var(--text-muted)' }}>
            Your basic account details are pre-filled from your signup information.
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input"
                value={`${user?.firstName || ''} ${user?.middleName || ''} ${user?.lastName || ''}`.trim()}
                disabled style={{ background: '#f8fafc' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input"
                value={user?.email || ''} disabled style={{ background: '#f8fafc' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile Number *</label>
              <input className="form-input" value={form.phone}
                onChange={handleMobileChange}
                placeholder="10-digit mobile number"
                maxLength={10} inputMode="numeric" required />
              {form.phone && form.phone.length !== 10 && (
                <span style={{ fontSize: 12, color: 'var(--danger)' }}>
                  Must be 10 digits ({form.phone.length}/10)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── SECTION 2: Contact Information ──────── */}
        <div className="card" style={{ padding: 28, marginBottom: 20 }}>
          <div style={sectionTitle}>2. Contact Information</div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Residential Address</label>
            <textarea className="form-input" value={form.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="Full residential address" rows={2}
              style={{ resize: 'vertical' }} />
          </div>
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city}
                onChange={(e) => set('city', e.target.value)} placeholder="City" />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <select className="form-input" value={form.state}
                onChange={(e) => set('state', e.target.value)}>
                <option value="">-- Select State --</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Nationality</label>
              <input className="form-input" value={form.nationality}
                onChange={(e) => set('nationality', e.target.value)}
                placeholder="e.g. Indian" />
            </div>
          </div>
          <div className="grid-3" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="form-label">Pin Code</label>
              <input className="form-input" value={form.pincode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  set('pincode', val);
                }}
                placeholder="6-digit pincode" maxLength={6} inputMode="numeric" />
            </div>
          </div>
          <div className="grid-3" style={{ marginTop: 16 }}>
          </div>
        </div>

        {/* ── SECTION 3: Personal Information ─────── */}
        <div className="card" style={{ padding: 28, marginBottom: 20 }}>
          <div style={sectionTitle}>3. Personal Information</div>
          <div className="grid-3" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Date of Birth (DD/MM/YYYY)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select className="form-input" value={form.dobDay} onChange={handleDOBPartChange('dobDay')}
                  style={{ flex: 1 }}>
                  <option value="">DD</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={String(d).padStart(2, '0')}>{String(d).padStart(2, '0')}</option>
                  ))}
                </select>
                <select className="form-input" value={form.dobMonth} onChange={handleDOBPartChange('dobMonth')}
                  style={{ flex: 1 }}>
                  <option value="">MM</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={String(m).padStart(2, '0')}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
                <select className="form-input" value={form.dobYear} onChange={handleDOBPartChange('dobYear')}
                  style={{ flex: 1.3 }}>
                  <option value="">YYYY</option>
                  {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {form.dateOfBirth && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Selected: {form.dobDay}/{form.dobMonth}/{form.dobYear}
                </span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Age</label>
              <input type="number" className="form-input" value={form.age}
                onChange={(e) => set('age', e.target.value)}
                placeholder="Auto-calculated from DOB"
                min={0} max={120} />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-input" value={form.gender}
                onChange={(e) => set('gender', e.target.value)}>
                <option value="">-- Select Gender --</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Photo</label>
              <input type="file" className="form-input"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handlePhotoChange}
                style={{ padding: '7px 12px', cursor: 'pointer' }} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                JPG or PNG, max 2MB
              </span>
              {photoPreview && (
                <div style={{ marginTop: 10 }}>
                  <img src={photoPreview} alt="Preview"
                    style={{ width: 80, height: 80, borderRadius: '50%',
                      objectFit: 'cover', border: '2px solid var(--border)' }} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── SECTION 4: Professional Information ─── */}
        <div className="card" style={{ padding: 28, marginBottom: 20 }}>
          <div style={sectionTitle}>4. Professional Information</div>
          <div className="grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Degree</label>
              <input className="form-input" value={form.degree}
                onChange={(e) => set('degree', e.target.value)}
                placeholder="e.g. M.D., M.B.B.S., Ph.D." />
            </div>
            <div className="form-group">
              <label className="form-label">University</label>
              <input className="form-input" value={form.university}
                onChange={(e) => set('university', e.target.value)}
                placeholder="University / Institution name" />
            </div>
          </div>
          <div className="grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Year of Passing</label>
              <input type="number" className="form-input" value={form.yearOfPassing}
                onChange={(e) => set('yearOfPassing', e.target.value)}
                placeholder="e.g. 2010"
                min={1950} max={new Date().getFullYear()} />
            </div>
            <div className="form-group">
              <label className="form-label">Membership Type</label>
              <select className="form-input" value={form.membershipType}
                onChange={(e) => set('membershipType', e.target.value)}>
                <option value="">-- Select Membership Type --</option>
                {MEMBERSHIP_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Areas of Expertise</label>
            <textarea className="form-input" value={form.areasOfInterest}
              onChange={(e) => set('areasOfInterest', e.target.value)}
              placeholder="e.g. Child Psychiatry, Geriatric Psychiatry, Addiction Medicine..."
              rows={2} style={{ resize: 'vertical' }} />
          </div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Clinic / Practice Address</label>
            <textarea className="form-input" value={form.officeAddress}
              onChange={(e) => set('officeAddress', e.target.value)}
              placeholder="Full clinic or practice address"
              rows={2} style={{ resize: 'vertical' }} />
          </div>

          {/* Scanned Documents Upload */}
          <div className="form-group">
            <label className="form-label">Scanned Documents</label>
            <div style={{
              border: '2px dashed var(--border)', borderRadius: 8,
              padding: '20px', textAlign: 'center', background: '#f8fafc'
            }}>
              <input type="file" multiple
                accept=".pdf,image/jpeg,image/png,image/jpg"
                onChange={handleScannedDegreesChange}
                style={{ display: 'block', width: '100%', cursor: 'pointer' }} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
                Upload: UG Degree / PG Degree / Medical Council Registration / RCI Registration
                <br />PDF or image files accepted. Multiple files allowed.
              </div>
            </div>
            {scannedFiles.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {scannedFiles.map((f, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', background: '#dbeafe',
                    borderRadius: 6, fontSize: 13, marginBottom: 4
                  }}>
                    <span>📄</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 500 }}>{f.name}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      ({(f.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── SECTION 5: Referral Selection ───────── */}
        <div className="card" style={{ padding: 28, marginBottom: 24 }}>
          <div style={sectionTitle}>5. Referral Selection *</div>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
            Select two active members as your referrals. Both must approve your request
            before it proceeds to Admin review. Search by name or member ID.
          </p>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Referral 1 *</label>
              <Select
                options={memberOptions.filter(
                  (opt) => !form.referral2 || opt.value !== form.referral2.value
                )}
                value={form.referral1}
                onChange={(val) => set('referral1', val)}
                onInputChange={handleReferralSearch}
                isLoading={searchLoading}
                placeholder="Type to search member..."
                isClearable
                noOptionsMessage={() => 'Type to search active members'}
                styles={selectStyles}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Referral 2 *</label>
              <Select
                options={memberOptions.filter(
                  (opt) => !form.referral1 || opt.value !== form.referral1.value
                )}
                value={form.referral2}
                onChange={(val) => set('referral2', val)}
                onInputChange={handleReferralSearch}
                isLoading={searchLoading}
                placeholder="Type to search member..."
                isClearable
                noOptionsMessage={() => 'Type to search active members'}
                styles={selectStyles}
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" className="btn btn-primary" disabled={loading}
            style={{ padding: '12px 32px' }}>
            {loading ? 'Submitting...' : 'Submit Membership Registration'}
          </button>
          <button type="button" className="btn btn-outline"
            onClick={() => navigate('/dashboard')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
